import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { getMasterProjects, getSubProjects, useGetProjectsQuery } from 'features/projects';
import { extractErrorMessage } from 'shared/utils/extractErrorMessage';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import { useDuplicateScenarioMutation } from '../../api/scenarios';
import type { AgentTestScenario } from '../../model/scenario';

const DUPLICATE_ERROR_FALLBACK = 'No se pudo duplicar el escenario. Intenta nuevamente.';
const NO_SUB_PROJECT = 'none';

interface ScenarioDuplicateDialogProps {
  scenario: AgentTestScenario | null;
  defaultProjectId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ScenarioDuplicateDialog = ({
  scenario,
  defaultProjectId,
  open,
  onOpenChange,
}: ScenarioDuplicateDialogProps): JSX.Element => {
  const [targetProjectId, setTargetProjectId] = useState<number>(defaultProjectId);
  const [targetSubProjectId, setTargetSubProjectId] = useState<string>(NO_SUB_PROJECT);
  const [name, setName] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: projects = [] } = useGetProjectsQuery();
  const masterProjects = useMemo(() => getMasterProjects(projects), [projects]);
  const targetSubProjects = useMemo(() => getSubProjects(projects, targetProjectId), [projects, targetProjectId]);

  const [duplicateScenario, { isLoading }] = useDuplicateScenarioMutation();

  useEffect(() => {
    if (!open || !scenario) return;
    setTargetProjectId(defaultProjectId);
    setTargetSubProjectId(NO_SUB_PROJECT);
    setName(`${scenario.name} (copy)`);
    setSubmitError(null);
  }, [open, scenario, defaultProjectId]);

  const handleClose = (): void => {
    setSubmitError(null);
    onOpenChange(false);
  };

  const handleSubmit = async (): Promise<void> => {
    if (!scenario) return;
    setSubmitError(null);
    try {
      await duplicateScenario({
        scenarioId: scenario.id,
        body: {
          projectId: targetProjectId,
          subProjectId: targetSubProjectId === NO_SUB_PROJECT ? null : Number(targetSubProjectId),
          ...(name.trim().length > 0 ? { name: name.trim() } : {}),
        },
      }).unwrap();
      handleClose();
    } catch (err) {
      setSubmitError(extractErrorMessage(err, DUPLICATE_ERROR_FALLBACK));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => (isOpen ? onOpenChange(true) : handleClose())}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Duplicar escenario</DialogTitle>
          <DialogDescription>
            Crea una copia de «{scenario?.name}» en otra inmobiliaria (o en la misma). La copia queda con alcance de
            inmobiliaria o de proyecto.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="duplicate-target-project">Inmobiliaria destino</Label>
            <Select
              value={String(targetProjectId)}
              onValueChange={(value) => {
                setTargetProjectId(Number(value));
                setTargetSubProjectId(NO_SUB_PROJECT);
              }}
            >
              <SelectTrigger id="duplicate-target-project">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {masterProjects.map((project) => (
                  <SelectItem key={project.id} value={String(project.id)}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="duplicate-target-subproject">Proyecto (opcional)</Label>
            <Select value={targetSubProjectId} onValueChange={setTargetSubProjectId}>
              <SelectTrigger id="duplicate-target-subproject">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_SUB_PROJECT}>Sin proyecto (alcance inmobiliaria)</SelectItem>
                {targetSubProjects.map((sub) => (
                  <SelectItem key={sub.id} value={String(sub.id)}>
                    {sub.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="duplicate-name">Nombre de la copia</Label>
            <Input id="duplicate-name" value={name} onChange={(event) => setName(event.target.value)} maxLength={255} />
          </div>

          {submitError ? (
            <div className="rounded-md border border-error/30 bg-error-light px-3 py-2 text-sm text-error">
              {submitError}
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={() => {
              void handleSubmit();
            }}
            disabled={isLoading}
          >
            {isLoading ? 'Duplicando…' : 'Duplicar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ScenarioDuplicateDialog;
